import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { SelectOption } from '@synqo/frontend-component-library';

export interface IHasTableModes<T extends string> {
    tableModeValue: T;
    setTableMode: (mode: T) => boolean;
    availableTableModes: SelectOption<T>[];
}

export function useTableMode<T extends string>(
    defaultMode: T,
    allTableModes: SelectOption<T>[],
    filterCallback?: (mode: T) => boolean,
): IHasTableModes<T> {
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter available modes if callback provided
    const filteredModes = useMemo(() => {
        if (!filterCallback) return allTableModes;
        return allTableModes.filter((mode) => filterCallback(mode.value));
    }, [allTableModes, filterCallback]);

    let tableMode = (searchParams.get('mode') ?? defaultMode) as T;
    const filteredTableModeNames = useMemo(() => {
        return filteredModes.map((mode) => mode.value);
    }, [filteredModes]);

    // If current mode is not in filtered modes, use default
    if (!filteredTableModeNames.includes(tableMode)) {
        tableMode = defaultMode;
    }

    const setTableMode = useCallback(
        (mode: T) => {
            // Check if mode is in filtered modes
            if (!filteredTableModeNames.includes(mode)) {
                return false;
            }
            setSearchParams({ mode: mode }, { replace: true });
            return true;
        },
        [filteredTableModeNames, setSearchParams],
    );

    return {
        tableModeValue: tableMode,
        setTableMode,
        availableTableModes: filteredModes,
    };
}
